import { Fragment, useEffect, useState } from 'react'
import { ExternalLink, ImageOff, Link2 } from 'lucide-react'
import type { CoupangDraftMeta } from '@/api/client'
import { autoApproveEta, signalLabel, signalTone, toneLabel, won } from '@/lib/coupang'
import { useAutoApproveLastRun } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { PlatformMark, TonePill, When } from '@/ui'

/** The draft's text with the partner link's place marked: the real link is
 *  made at publish time, so the placeholder shows as a chip. */
/** The product's plain page. Never `product_url`: the Partners API hands that
 *  out with the partner tag on it, and opening it from the console would count
 *  as a click on our own affiliate link. */
export const productPage = (coupangProductId: string) =>
  `https://www.coupang.com/vp/products/${encodeURIComponent(coupangProductId)}`

export function CoupangBody({ text, compact }: { text: string; compact?: boolean }) {
  const parts = text.split('{{link}}')
  return (
    <p className={cn('whitespace-pre-wrap break-words text-body text-foreground', compact && 'line-clamp-6')}>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <span className="mx-0.5 inline-flex h-5 items-center gap-1 rounded-full bg-accent/10 px-2 align-middle text-micro font-medium text-accent">
              <Link2 size={11} aria-hidden />
              쿠팡 링크
            </span>
          )}
        </Fragment>
      ))}
    </p>
  )
}

function Thumb({ src }: { src?: string }) {
  const [broken, setBroken] = useState(false)
  if (!src || broken) {
    return (
      <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
        <ImageOff size={18} aria-hidden />
      </span>
    )
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      className="h-16 w-16 shrink-0 rounded-md border border-border object-cover"
    />
  )
}

/** The product a Coupang draft is about: picture, name, price, the signal that
 *  made it worth a post and the plain product page (not the partner link). */
export function CoupangProduct({ meta }: { meta: CoupangDraftMeta }) {
  const off = meta.original_price != null && meta.price != null && meta.original_price > meta.price
  return (
    <div className="flex gap-3 rounded-lg border border-border bg-muted/30 p-3">
      <Thumb src={meta.image_url} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="line-clamp-2 text-body font-medium">{meta.name || meta.hook || '상품'}</p>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 tabular-nums">
          <span className="text-body font-semibold">{won(meta.price)}</span>
          {off && <span className="text-meta text-muted-foreground line-through">{won(meta.original_price)}</span>}
          {meta.discount_rate != null && meta.discount_rate > 0 && (
            <span className="text-meta font-medium text-status-failed">{meta.discount_rate}%</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-muted-foreground">
          <TonePill tone={signalTone(meta.signal_kind)} label={signalLabel(meta.signal_kind)} />
          <span>
            <When iso={meta.detected_at} mode="relative" /> 감지
          </span>
          {meta.coupang_product_id && (
            <a
              href={productPage(meta.coupang_product_id)}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-accent hover:underline"
            >
              쿠팡에서 보기
              <ExternalLink size={12} aria-hidden />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/** How a Coupang draft reads: the Threads bubble with the link's place marked,
 *  and the product under it. The ad judge does not run for these, so there is
 *  no score. */
export function CoupangPost({
  meta,
  body,
  account,
  compact,
  className,
}: {
  meta: CoupangDraftMeta
  body: string
  account?: string
  compact?: boolean
  className?: string
}) {
  return (
    <div className={cn('space-y-3', className)}>
      <header className="flex items-center gap-2 text-meta">
        <PlatformMark platform="threads" />
        <span className="font-semibold text-foreground">{account ? `@${account}` : 'Threads'}</span>
        <span className="ml-auto text-muted-foreground">쿠팡 · {toneLabel(meta.tone)}</span>
      </header>
      <div className="rounded-2xl rounded-tl-sm bg-muted/60 px-3.5 py-3">
        <CoupangBody text={body} compact={compact} />
      </div>
      {meta.tone === 'review' && (
        <p className="text-meta text-muted-foreground">후기형이라 링크는 답글로 단다. 본문에는 링크가 없다.</p>
      )}
      <CoupangProduct meta={meta} />
    </div>
  )
}

/** On a Coupang draft the auto-approve tick will take: when that happens, so a
 *  draft that sits in review for a few minutes doesn't read as waiting on a
 *  person. Approving it by hand still works. */
export function AutoApproveNote() {
  const last = useAutoApproveLastRun().data
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(t)
  }, [])
  const eta = autoApproveEta(last, now)
  if (eta.kind === 'stalled') {
    return (
      <span title={`자동 승인 작업이 ${eta.minutes}분째 돌지 않았다. 직접 승인하거나 작업 화면을 본다.`}>
        <TonePill tone="failed" label="자동 승인 멈춤" />
      </span>
    )
  }
  const label = eta.kind === 'in' ? `약 ${eta.minutes}분 뒤 자동 승인` : eta.kind === 'soon' ? '곧 자동 승인' : '자동 승인 대기'
  return (
    <span title="쿠팡 글은 10분마다 자동으로 승인된다. 기다리면 슬롯을 받고, 지금 승인해도 된다.">
      <TonePill tone="scheduled" label={label} />
    </span>
  )
}
